import { generateRandomRgbColor } from "utils/generateRandomRgbColor";
import type { PlanV3, PlanRouteGroupV3 } from "./types";

interface MoveRouteBetweenGroupsArgs {
  plan: PlanV3;
  routeId: string;
  fromGroupLabel: string;
  toGroupLabel: string;
  newRouteTypeId: number;
}

export const moveRouteBetweenGroups = ({
  plan,
  routeId,
  fromGroupLabel,
  toGroupLabel,
  newRouteTypeId,
}: MoveRouteBetweenGroupsArgs): PlanV3 => {
  const sourceGroup = plan.routeGroups.find(
    (group) => group.label === fromGroupLabel,
  );
  if (!sourceGroup) return plan;

  const route = sourceGroup.routes.find((route) => route.id === routeId);
  if (!route) return plan;

  const targetGroup = plan.routeGroups.find(
    (group) => group.label === toGroupLabel,
  );
  // Status and LV belong to the cable type, so the route takes the target group's.
  const sibling = targetGroup?.routes[0];
  const movedRoute = {
    ...route,
    route_type_id: newRouteTypeId,
    cable_type: toGroupLabel,
    cable_type_status: sibling?.cable_type_status ?? "confident",
    lv_position: sibling?.lv_position ?? "",
    lvMatch: sibling?.lvMatch,
  };

  const updatedSourceRoutes = sourceGroup.routes.filter(
    (route) => route.id !== routeId,
  );
  const sourceEmpty = updatedSourceRoutes.length === 0;

  const routeGroups: PlanRouteGroupV3[] = [];

  for (const group of plan.routeGroups) {
    if (group.label === fromGroupLabel) {
      if (!sourceEmpty) {
        routeGroups.push({
          ...group,
          routes: updatedSourceRoutes,
          meters: updatedSourceRoutes.reduce(
            (sum, route) => sum + (Number(route.meters) || 0),
            0,
          ),
        });
      }
    } else if (group.label === toGroupLabel) {
      const mergedRoutes = [...group.routes, movedRoute];
      routeGroups.push({
        ...group,
        routes: mergedRoutes,
        meters: mergedRoutes.reduce(
          (sum, route) => sum + (Number(route.meters) || 0),
          0,
        ),
      });
    } else {
      routeGroups.push(group);
    }
  }

  if (!targetGroup) {
    routeGroups.push({
      route_type_id: newRouteTypeId,
      label: toGroupLabel,
      meters: Number(movedRoute.meters) || 0,
      routes: [movedRoute],
      rgb: generateRandomRgbColor(),
      // Matches backend: route types created via the route reassign endpoint are CONFIDENT
      cableTypeStatus: "confident",
    });
  }

  return { ...plan, routeGroups };
};
